/**
 * Mock Data Service
 * Provides mocked API responses for faster development and testing
 */

class MockDataService {
  constructor() {
    this.isEnabled = this.shouldUseMockData();
    this.cacheDir = '/workspace/mock-data';
  }

  shouldUseMockData() {
    // Only enable when explicitly opted in via the build-time flag
    // (set by `yarn start:mock` and the e2e test scripts via cross-env).
    // Plain `yarn start` must hit the real API.
    return process.env.USE_MOCK_DATA === 'true';
  }

  /**
   * Load mock data from files
   */
  async loadMockData(filename) {
    try {
      const response = await fetch(`/mock-data/${filename}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn(`[MockData] Could not load mock data from ${filename}:`, error.message);
    }
    return null;
  }

  /**
   * Generate mock data for common API endpoints
   */
  async generateMockData(url) {
    // Try to load mock data from files first
    if (url.includes('/svc/most/')) {
      const mockData = await this.loadMockData('most.json');
      if (mockData) return mockData;
      return this.generateMostData();
    }

    if (url.includes('/svc/data/')) {
      // Extract year from URL
      const yearMatch = url.match(/year1=(\d+)/);
      const year = yearMatch ? yearMatch[1] : '2023';

      const mockData = await this.loadMockData(`data-${year}.json`);
      if (mockData) return mockData;
      return this.generateDataResponse(url);
    }

    if (url.includes('/svc/publicviews')) {
      const mockData = await this.loadMockData('publicviews.json');
      if (mockData) return mockData;
      return this.generatePublicViewsData();
    }

    if (url.includes('/svc/external')) {
      const mockData = await this.loadMockData('external.json');
      if (mockData) return mockData;
      return this.generateExternalData();
    }

    // Default empty response
    return { data: [], message: 'Mock data not available' };
  }

  generateMostData() {
    return {
      countries: [
        { id: 'BI', name: 'Burundi', level: 1 },
        { id: 'ZA', name: 'South Africa', level: 1 },
        { id: 'KE', name: 'Kenya', level: 1 },
        { id: 'NG', name: 'Nigeria', level: 1 },
        { id: 'EG', name: 'Egypt', level: 1 },
      ],
      measures: [
        { id: 'PRI', name: 'Primary Measure', level: 1 },
        { id: 'SROL', name: 'Secondary Measure', level: 1 },
        { id: 'FEO', name: 'Feature Measure', level: 1 },
      ],
      descriptors: [],
      groups: [],
      strings: [],
    };
  }

  generateDataResponse(url) {
    // Parse year from URL
    const yearMatch = url.match(/year1=(\d+)/);
    const year = yearMatch ? parseInt(yearMatch[1]) : 2023;

    // Generate mock data for the specified year
    const locations = ['BI', 'ZA', 'KE', 'NG', 'EG'];
    const measures = ['PRI', 'SROL', 'FEO'];

    const data = [];

    locations.forEach((loc) => {
      measures.forEach((meas) => {
        data.push({
          location: loc,
          measure: meas,
          year: year,
          score: Math.random() * 100,
          rank: Math.floor(Math.random() * 54) + 1,
          trend: Math.random() > 0.5 ? 'up' : 'down',
        });
      });
    });

    return {
      data: data,
      year: year,
      total: data.length,
      generated_at: new Date().toISOString(),
    };
  }

  generatePublicViewsData() {
    return {
      views: [
        {
          id: 1,
          name: 'Test View 1',
          meas: 'PRI',
          loc: 'BI,ZA',
          view: 'overview',
          subview: 'absoluteTrends',
        },
        {
          id: 2,
          name: 'Test View 2',
          meas: 'SROL',
          loc: 'KE,NG',
          view: 'table',
          subview: 'score',
        },
      ],
    };
  }

  generateExternalData() {
    return {
      external_sources: [
        {
          name: 'World Bank',
          url: 'https://worldbank.org',
          description: 'World Bank data',
        },
      ],
    };
  }

  /**
   * Intercept fetch requests and return mock data when available
   * Note: This is now handled by fetchWithRetry, but kept for compatibility
   */
  interceptFetch() {
    // No longer needed - handled by fetchWithRetry
  }
}

// Create singleton instance
const mockDataService = new MockDataService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.mockDataService = mockDataService;
}

export default mockDataService;
